import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OVERVIEW_TAB } from './eventPaneTabs';

export type TabType = 'tools' | 'chat' | 'overview' | 'data' | 'transcript';
type ToolType = 'air_ambulance' | 'ali_discrepancy';

/**
 * Payload for selected tool in the event pane.
 */
interface SelectedToolPayload {
  /**
   * Which tool is selected
   */
  tool: ToolType;
  /**
   * Optional properties specific to the selected tool.
   * This allows passing data to the tool when it is selected.
   * Each tool defines its own expected properties structure and is responsible for interpreting them.
   */
  toolProperties?: unknown;
}

type SelectedTool = SelectedToolPayload | null;

export type ChatType =
  | 'isAgencyShareChatVisible'
  | 'isTextFrom911ChatVisible'
  | 'isRcsChatVisible'
  | 'isConversationsChatVisible'
  | 'isCollaboratorChatVisible';

export interface EventPaneState {
  selectedTab: TabType;
  selectedTool: SelectedTool;
  isCollapsed: boolean;
  chatVisibility: {
    isAgencyShareChatVisible: boolean;
    isTextFrom911ChatVisible: boolean;
    isRcsChatVisible: boolean;
    isConversationsChatVisible: boolean;
    isCollaboratorChatVisible: boolean;
  };
}

const initialState: EventPaneState = {
  selectedTab: OVERVIEW_TAB,
  selectedTool: null as SelectedTool,
  isCollapsed: false,
  chatVisibility: {
    isAgencyShareChatVisible: true,
    isTextFrom911ChatVisible: true,
    isRcsChatVisible: true,
    isConversationsChatVisible: true,
    isCollaboratorChatVisible: true,
  },
};

const eventPaneSlice = createSlice({
  name: 'eventPane',
  initialState,
  reducers: {
    setSelectedTab: (state, action: PayloadAction<TabType>) => {
      state.selectedTab = action.payload;
    },
    setSelectedTool: (state, action: PayloadAction<SelectedTool>) => {
      state.selectedTool = action.payload;
    },
    setChatVisibility: (
      state,
      action: PayloadAction<{
        chatType: ChatType;
        isVisible: boolean;
      }>,
    ) => {
      const { chatType, isVisible } = action.payload;
      state.chatVisibility[chatType] = isVisible;
    },
    collapse: state => {
      state.isCollapsed = true;
    },
    expand: state => {
      state.isCollapsed = false;
    },
    resetState: () => initialState,
  },
});

export const {
  setSelectedTab,
  setSelectedTool,
  setChatVisibility,
  collapse,
  expand,
  resetState,
} = eventPaneSlice.actions;
export type { ToolType, SelectedTool };
export default eventPaneSlice.reducer;
